import { useUnsavedChanges, useConfirmLeave } from './UnsavedChanges';
import { useQueryClient } from '@tanstack/react-query';
import {
  downloadPrivateFile,
  orgID,
  post,
  postIdempotent,
  useData,
  useList,
  useSession,
} from '@yingbaba/api';
import { ArrowUpRight, Download, LockKeyhole, Plus } from '@yingbaba/icons';
import type {
  Artifact,
  Portfolio,
  Provider,
  ProviderVerification,
  Resource,
  ServicePackage,
  ServicePackageItem,
} from '@yingbaba/types';
import {
  ActionDialog,
  Button,
  ButtonLink,
  Empty,
  ErrorNotice,
  IconButton,
  Image,
  Notice,
  PageTitle,
  Pagination,
  portfolioCover,
  portfolioMediaLabel,
  PortfolioPreview,
  QueryState,
  Select,
  Status,
  TagList,
  useToast,
} from '@yingbaba/ui';
import { categories, cityOptions, collaborationOnly, date, money, siteURL } from '@yingbaba/utils';
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PortfolioEditor } from './PortfolioEditor';
import { StructuredBriefEditor } from './StructuredBrief';
import { creativeKinds, CreativeDraftDialog } from './CreativeTools';
import { useArtifactCreate } from './artifact-command';

type ServicePackageView = {
  package: ServicePackage;
  items: ServicePackageItem[];
};

function servicePackagePayload(v: Record<string, string>) {
  return {
    name: v.name,
    category: v.category,
    summary: v.summary,
    pricing_mode: collaborationOnly ? 'custom' : v.pricing_mode,
    price_min_minor: collaborationOnly ? 0 : Math.round(Number(v.price_min || 0) * 100),
    price_max_minor: collaborationOnly ? 0 : Math.round(Number(v.price_max || 0) * 100),
    estimated_days: Number(v.estimated_days),
    shoot_days: Number(v.shoot_days || 0),
    revision_limit: Number(v.revision_limit || 0),
    deliverables: v.deliverables || '',
    crew_description: v.crew_description || '',
    equipment_description: v.equipment_description || '',
    rights_description: v.rights_description || '',
    items: [
      {
        type: 'service',
        name: v.name,
        description: v.deliverables || v.summary,
        quantity: 1,
        unit: '项目',
      },
    ],
  };
}

function packageFields(pkg?: ServicePackage) {
  return [
    { name: 'name', label: '服务产品名称', value: pkg?.name },
    { name: 'category', label: '服务分类', options: categories, value: pkg?.category },
    {
      name: 'summary',
      label: '服务产品介绍',
      type: 'textarea',
      min: 20,
      value: pkg?.summary,
    },
    {
      name: 'pricing_mode',
      label: '定价方式',
      value: pkg?.pricing_mode || 'range',
      options: [
        { value: 'fixed', label: '固定价' },
        { value: 'starting_from', label: '起步价' },
        { value: 'range', label: '价格区间' },
        { value: 'custom', label: '按需求报价' },
      ],
    },
    {
      name: 'price_min',
      label: '最低 / 固定价格（元）',
      type: 'number',
      min: 0,
      step: 0.01,
      value: String((pkg?.price_min_minor || 0) / 100),
    },
    {
      name: 'price_max',
      label: '最高价格（元，非区间可留 0）',
      type: 'number',
      min: 0,
      step: 0.01,
      required: false,
      value: String((pkg?.price_max_minor || 0) / 100),
    },
    {
      name: 'estimated_days',
      label: '预计交付周期（天）',
      type: 'number',
      min: 1,
      value: String(pkg?.estimated_days || 30),
    },
    {
      name: 'shoot_days',
      label: '预计拍摄天数',
      type: 'number',
      min: 0,
      value: String(pkg?.shoot_days || 1),
    },
    {
      name: 'revision_limit',
      label: '包含修改轮次',
      type: 'number',
      min: 0,
      value: String(pkg?.revision_limit ?? 2),
    },
    {
      name: 'deliverables',
      label: '标准交付物',
      type: 'textarea',
      required: false,
      value: pkg?.deliverables,
    },
    {
      name: 'crew_description',
      label: '团队配置',
      type: 'textarea',
      required: false,
      value: pkg?.crew_description,
    },
    {
      name: 'equipment_description',
      label: '设备配置',
      type: 'textarea',
      required: false,
      value: pkg?.equipment_description,
    },
    {
      name: 'rights_description',
      label: '版权与使用范围',
      type: 'textarea',
      required: false,
      value: pkg?.rights_description,
    },
  ].filter(
    (f) => !collaborationOnly || !['pricing_mode', 'price_min', 'price_max'].includes(f.name),
  );
}

export function CreativeStudio() {
  const [params, setParams] = useSearchParams();
  const page = Math.min(10000, Math.max(1, Math.floor(Number(params.get('page')) || 1)));
  const query = params.get('q') || '';
  const kind = params.get('kind') || '';
  const status = params.get('status') || '';
  const selected = params.get('artifact') || '';
  const [createOpen, setCreateOpen] = useState(false);
  const [savedSelection, setSavedSelection] = useState('');
  const [briefDirtyID, setBriefDirtyID] = useState('');
  const listPath = `/workspace/artifacts?${new URLSearchParams({ page: String(page), q: query, kind, status })}`;
  const list = useList<Artifact>(listPath);
  const session = useSession();
  const organization = session.data?.organizations.find((org) => org.id === orgID());
  const canWrite =
    ['owner', 'admin', 'manager', 'member'].includes(organization?.role || '') &&
    (!organization?.lifecycle_status || organization.lifecycle_status === 'active') &&
    session.data?.account.lifecycle_status !== 'closing';
  const meta = useData<{ features: { ai: boolean } }>('/meta');
  const [drafts, setDrafts] = useState<Record<string, string>>({}),
    [error, setError] = useState<unknown>(null),
    [busy, setBusy] = useState(false);
  const qc = useQueryClient(),
    toast = useToast();
  const listed = list.data?.items.find((v) => v.id === selected);
  const detail = useData<Artifact>(
    `/artifacts/${selected}`,
    !!selected && !listed && !list.isPending,
  );
  const current = selected ? listed || detail.data : list.data?.items[0];
  const createArtifact = useArtifactCreate();
  function updateFilters(values: Record<string, string>, replace = false) {
    setParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        for (const [key, value] of Object.entries(values))
          value ? next.set(key, value) : next.delete(key);
        return next;
      },
      { replace },
    );
  }
  // Commit clean editor state before changing the URL, so saved versions do not trigger a leave prompt.
  useEffect(() => {
    if (!savedSelection) return;
    updateFilters({ artifact: savedSelection, page: '', q: '', kind: '', status: '' }, true);
    setSavedSelection('');
  }, [savedSelection]);
  const content = current ? (drafts[current.id] ?? current.content ?? '') : '';
  const setContent = (value: string) => {
    if (current) setDrafts((all) => ({ ...all, [current.id]: value }));
  };
  const generating = !!current && ['queued', 'running'].includes(current.status);
  const canEdit = canWrite && !generating && !!current && current.kind !== 'meeting_actions';
  const pending = list.data?.items.some((v) => ['queued', 'running'].includes(v.status));
  useData(listPath, !!pending, 3000);
  useData<Artifact>(`/artifacts/${selected}`, !!selected && !listed && generating, 3000);
  const leave = useConfirmLeave();
  const mutationLock = useRef(false);
  const retryAttempt = useRef({ id: '', key: '' });
  useUnsavedChanges({
    dirty: canEdit && content !== (current?.content || ''),
    busy,
    save: () => saveVersion(false),
    discard: () => {
      if (current)
        setDrafts((all) => {
          const next = { ...all };
          delete next[current.id];
          return next;
        });
    },
  });
  async function saveVersion(select = true): Promise<boolean> {
    if (!current || mutationLock.current || !canEdit || !content.trim()) return false;
    mutationLock.current = true;
    setBusy(true);
    setError(null);
    try {
      const v = await createArtifact({
        title: current.title,
        kind: current.kind,
        input: current.input,
        content,
        parent_id: current.id,
      });
      await qc.invalidateQueries();
      if (select) setSavedSelection(v.id);
      setDrafts((all) => {
        const next = { ...all };
        delete next[current.id];
        return next;
      });
      toast('新版本已保存');
      return true;
    } catch (e) {
      setError(e);
      return false;
    } finally {
      mutationLock.current = false;
      setBusy(false);
    }
  }
  async function retryGeneration() {
    if (!current || mutationLock.current || !canWrite) return;
    mutationLock.current = true;
    if (retryAttempt.current.id !== current.id)
      retryAttempt.current = { id: current.id, key: crypto.randomUUID() };
    setBusy(true);
    setError(null);
    try {
      await postIdempotent(`/artifacts/${current.id}/retry`, {}, retryAttempt.current.key);
      retryAttempt.current = { id: '', key: '' };
      setDrafts((all) => {
        const next = { ...all };
        delete next[current.id];
        return next;
      });
      await qc.invalidateQueries();
      toast('已重新提交生成任务');
    } catch (reason) {
      setError(reason);
    } finally {
      mutationLock.current = false;
      setBusy(false);
    }
  }
  function exportText() {
    if (!current || generating || !content.trim()) return;
    const b = new Blob([content], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(b);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${current.title.replace(/[\\/:*?"<>|]/g, '_')}-v${current.version}.md`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
  return (
    <>
      <PageTitle
        title="AI 创作工作台"
        description="让灵感成为清晰的方案，把每一次创作认真保存。"
        action={
          canWrite && (
            <Button onClick={() => leave(() => setCreateOpen(true))}>
              <Plus size={16} />
              开始新的创作
            </Button>
          )
        }
      />
      {createOpen && (
        <CreativeDraftDialog
          ai={!!meta.data?.features.ai}
          onClose={() => setCreateOpen(false)}
          onCreated={(value) => {
            setCreateOpen(false);
            setSavedSelection(value.id);
            toast('创作已建立');
          }}
        />
      )}
      <div className="creative-layout">
        <aside className="stack creative-sidebar">
          <Notice>
            {meta.data?.features.ai
              ? '生成内容可修改并保存版本，正式使用前请确认事实与创意。'
              : 'AI 服务尚未开启。你可以先使用结构化模板，手动编辑创作需求、脚本与分镜。'}
          </Notice>
          <section className="panel">
            <h3>我的创作版本</h3>
            <form
              className="creative-search form-stack"
              key={`${query}:${kind}:${status}`}
              role="search"
              aria-label="查找创作版本"
              onSubmit={(event) => {
                event.preventDefault();
                const form = new FormData(event.currentTarget);
                updateFilters({ q: String(form.get('q') || '').trim(), page: '', artifact: '' });
              }}
            >
              <div className="creative-search-row">
                <input
                  className="input"
                  name="q"
                  aria-label="搜索创作标题"
                  placeholder="搜索创作标题"
                  defaultValue={query}
                  maxLength={200}
                />
                <Button type="submit" variant="secondary">
                  搜索
                </Button>
              </div>
              <Select
                aria-label="创作类型筛选"
                value={kind}
                onChange={(value) => updateFilters({ kind: value, page: '', artifact: '' })}
              >
                <option value="">全部类型</option>
                {creativeKinds.map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </Select>
              <Select
                aria-label="创作状态筛选"
                value={status}
                onChange={(value) => updateFilters({ status: value, page: '', artifact: '' })}
              >
                <option value="">全部状态</option>
                <option value="draft">草稿</option>
                <option value="generating">生成中</option>
                <option value="succeeded">生成完成</option>
                <option value="failed">生成失败</option>
                <option value="cancelled">已取消</option>
              </Select>
            </form>
            <QueryState query={list}>
              <div className="creative-version-list">
                {list.data?.items.map((a) => (
                  <button
                    key={a.id}
                    className={`version-item creative-version ${a.id === current?.id ? 'active' : ''}`}
                    aria-current={a.id === current?.id ? 'true' : undefined}
                    onClick={() => {
                      if (a.id !== current?.id || !selected) updateFilters({ artifact: a.id });
                    }}
                  >
                    <div>
                      <strong className="small">{a.title}</strong>
                      <p className="small muted">
                        V{a.version} · {date(a.created_at)}
                        {a.locked ? ' · 已锁定' : ''}
                      </p>
                    </div>
                    <Status value={a.status} />
                  </button>
                ))}
                {!list.data?.items.length && (
                  <p className="small muted">
                    {query || kind || status
                      ? '没有符合条件的创作，可调整关键词或筛选。'
                      : '还没有创作，点击上方按钮开始第一份草稿。'}
                  </p>
                )}
              </div>
              <Pagination
                page={page}
                total={list.data?.total || 0}
                size={list.data?.page_size || 30}
                onPage={(next) => {
                  updateFilters({ page: String(next), artifact: '' });
                }}
              />
            </QueryState>
          </section>
        </aside>
        <section className="stack">
          <ErrorNotice error={error || (selected && !listed ? detail.error : null)} />
          {current ? (
            <>
              <div className="panel">
                <div className="row spread wrap">
                  <div>
                    <span className="eyebrow">
                      VERSION {current.version} /{' '}
                      {current.provider === 'template'
                        ? '创作模板'
                        : current.provider === 'manual'
                          ? '人工编辑'
                          : 'AI 生成'}
                    </span>
                    <h3>{current.title}</h3>
                  </div>
                  <div className="row wrap">
                    {current.kind === 'brief' &&
                      !!current.structured_content &&
                      ['docx', 'pdf'].map((format) => (
                        <Button
                          key={format}
                          size="sm"
                          variant="secondary"
                          disabled={
                            generating || content !== current.content || briefDirtyID === current.id
                          }
                          onClick={async () => {
                            try {
                              await downloadPrivateFile(
                                `/artifacts/${current.id}/brief-export?format=${format}`,
                                `${current.title}-创作需求书.${format}`,
                                { direct: false },
                              );
                            } catch (e) {
                              setError(e);
                            }
                          }}
                        >
                          {format === 'pdf' ? '导出 PDF' : '导出 Word'}
                        </Button>
                      ))}
                    <IconButton
                      label="导出 Markdown"
                      onClick={exportText}
                      disabled={generating || !content.trim()}
                    >
                      <Download size={17} />
                    </IconButton>
                    {canEdit &&
                      !current.locked &&
                      content === (current.content || '') &&
                      briefDirtyID !== current.id &&
                      ['draft', 'succeeded'].includes(current.status) && (
                        <ActionDialog
                          title="锁定创作版本"
                          trigger={
                            <>
                              <LockKeyhole size={14} />
                              锁定
                            </>
                          }
                          variant="secondary"
                          path={`/artifacts/${current.id}/lock`}
                          submitLabel="锁定此版本"
                          description="锁定后当前版本只读，你仍可复制内容并保存新版本。"
                        />
                      )}
                  </div>
                </div>
              </div>
              {['failed', 'cancelled'].includes(current.status) && (
                <Notice tone={current.status === 'failed' ? 'error' : 'info'}>
                  <strong>{current.status === 'failed' ? '这次生成未完成' : '生成已停止'}</strong>
                  <p>
                    {current.error ||
                      (current.kind === 'meeting_actions'
                        ? '你可以重新生成，并在来源会议中确认行动项。'
                        : '你可以重新生成，或直接编辑内容并保存新版本。')}
                  </p>
                  {!!content.trim() && content !== current.content && (
                    <p>手动编辑的内容尚未保存，请先保存为新版本。</p>
                  )}
                  {canWrite && !current.locked && meta.data?.features.ai && (
                    <Button
                      variant="secondary"
                      size="sm"
                      loading={busy}
                      disabled={!!content.trim() && content !== current.content}
                      onClick={retryGeneration}
                    >
                      重新生成
                    </Button>
                  )}
                </Notice>
              )}
              {generating ? (
                <Notice>
                  创作正在进行，生成完成后会保存到此版本。
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => (selected && !listed ? detail.refetch() : list.refetch())}
                  >
                    查看进度
                  </Button>
                  {canWrite && current.current_run_id && (
                    <ActionDialog
                      title="停止本次生成"
                      trigger="停止生成"
                      variant="secondary"
                      path={`/artifacts/${current.id}/cancel`}
                      intent="destructive"
                      submitLabel="停止生成"
                      description="停止后可重新生成；已经发生的模型调用可能仍会产生费用。"
                      transform={() => ({ run_id: current.current_run_id })}
                    />
                  )}
                </Notice>
              ) : current.kind === 'meeting_actions' ? (
                <>
                  <Notice>
                    会议行动项需在来源项目中核对负责人和日期，再导入任务。
                    {current.project_id && (
                      <ButtonLink
                        variant="secondary"
                        to={`/projects/${current.project_id}?tab=meetings`}
                      >
                        前往来源会议
                      </ButtonLink>
                    )}
                  </Notice>
                  <textarea
                    className="creative-editor"
                    aria-label="会议行动项内容"
                    value={content}
                    readOnly
                  />
                </>
              ) : current.kind === 'brief' &&
                current.structured_version === 1 &&
                current.structured_content ? (
                <StructuredBriefEditor
                  key={current.id}
                  artifact={current}
                  onDirty={(dirty) => setBriefDirtyID(dirty ? current.id : '')}
                  write={canWrite}
                  onSaved={(id) => {
                    setSavedSelection(id);
                    setBriefDirtyID('');
                    toast('创作需求新版本已保存');
                  }}
                />
              ) : (
                <textarea
                  className="creative-editor"
                  aria-label="创作内容编辑器"
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  readOnly={current.locked || !canWrite || generating}
                />
              )}
              {current.kind !== 'meeting_actions' &&
                !(
                  current.kind === 'brief' &&
                  current.structured_version === 1 &&
                  current.structured_content
                ) && (
                  <div className="row spread wrap creative-editor-actions">
                    <span className="small muted">
                      {!canWrite
                        ? '当前组织角色可查看和导出内容'
                        : generating
                          ? '生成完成后即可编辑与保存'
                          : current.locked
                            ? '已锁定的版本，内容只读'
                            : '内容暂存在编辑器中，点击保存后写入新版本。'}
                    </span>
                    <Button
                      onClick={() => void saveVersion()}
                      loading={busy}
                      disabled={!canEdit || !content.trim()}
                    >
                      {current.locked ? '复制为新版本' : '保存新版本'}
                    </Button>
                  </div>
                )}
            </>
          ) : (
            <div className="panel creative-empty">
              <Empty
                title={
                  selected
                    ? detail.isPending
                      ? '正在打开创作…'
                      : '暂时无法打开这份创作'
                    : '每部好作品，都从一页空白开始。'
                }
                description={
                  selected
                    ? '可从创作列表重新选择，或重试打开。'
                    : '点击“开始新的创作”，记录想法并建立第一份草稿。'
                }
                action={
                  selected && !detail.isPending ? (
                    <Button variant="secondary" onClick={() => void detail.refetch()}>
                      重新加载
                    </Button>
                  ) : undefined
                }
              />
            </div>
          )}
        </section>
      </div>
    </>
  );
}
export function Supply({ kind }: { kind: string }) {
  const isProvider = kind === 'provider';
  const providers = useList<Provider>('/workspace/providers', isProvider);
  const [casePage, setCasePage] = useState(1);
  const resources = useList<Resource>('/workspace/resources?page_size=100', isProvider);
  const cases = useList<Portfolio>(`/workspace/cases?page=${casePage}`, isProvider);
  const session = useSession();
  const canWrite = ['owner', 'admin', 'manager', 'member'].includes(
    session.data?.organizations.find((org) => org.id === orgID())?.role || '',
  );
  const packages = useData<ServicePackageView[]>('/service-packages', isProvider);
  const verifications = useData<ProviderVerification[]>('/provider-verifications', isProvider);
  const [searchParams] = useSearchParams();
  const profile = providers.data?.items[0];
  return (
    <>
      <PageTitle
        title={'作品与服务'}
        description={'让好作品成为你的名片，让客户认识你的专业。'}
        action={
          <ButtonLink to={siteURL('www', '/providers')} variant="secondary">
            浏览市场
            <ArrowUpRight size={15} />
          </ButtonLink>
        }
      />
      {
        <>
          <section className="panel">
            <div className="panel-title">
              <h3>服务商主页</h3>
              {profile && <Status value={profile.status} />}
            </div>
            {profile ? (
              <div className="row" style={{ alignItems: 'flex-start', gap: 25 }}>
                <Image src={profile.cover} alt={profile.name} className="supply-cover" />
                <div style={{ minWidth: '50%' }}>
                  <h2>{profile.name}</h2>
                  <p className="muted" style={{ margin: '13px 0' }}>
                    {profile.summary}
                  </p>
                  <TagList value={profile.tags} />
                  <p className="small muted" style={{ margin: '15px 0' }}>
                    {profile.city} · {money(profile.starting_price_minor)} 起
                  </p>
                  {profile.review_reason && <Notice>{profile.review_reason}</Notice>}
                </div>
              </div>
            ) : (
              <Empty
                title="让合适的客户发现你"
                description="填写团队介绍、擅长类型与服务范围，提交入驻审核。"
              />
            )}
            {(!profile || profile.status !== 'pending_review') && (
              <div style={{ marginTop: 25 }}>
                <ActionDialog
                  title={profile ? '更新服务商资料' : '申请服务商入驻'}
                  trigger={profile ? '更新资料并提交审核' : '开始入驻'}
                  path="/providers/apply"
                  fields={[
                    { name: 'name', label: '团队 / 公司名称', value: profile?.name },
                    { name: 'city', label: '所在城市', options: cityOptions, value: profile?.city },
                    {
                      name: 'category',
                      label: '擅长类型',
                      options: categories,
                      value: profile?.category,
                    },
                    { name: 'summary', label: '一句话介绍', value: profile?.summary },
                    {
                      name: 'description',
                      label: '公司介绍与服务范围',
                      type: 'textarea',
                      min: 20,
                      value: profile?.description,
                    },
                    { name: 'tags', label: '能力标签（逗号分隔）', value: profile?.tags },
                    {
                      name: 'price',
                      label: '项目起步价（元）',
                      type: 'number',
                      min: 0,
                      value: String((profile?.starting_price_minor || 0) / 100),
                    },
                  ]}
                  transform={(v) => ({
                    ...v,
                    starting_price_minor: Math.round(Number(v.price) * 100),
                    cover: profile?.cover || '',
                  })}
                />
              </div>
            )}
          </section>
          <section className="panel" style={{ marginTop: 24 }}>
            <div className="panel-title">
              <div>
                <h3>认证与信用</h3>
                <p className="small muted" style={{ marginTop: 7 }}>
                  主体认证决定交易准入；这里的经营资质与作品真实性核验是服务商额外信用证明，不替代个人/企业主体认证。
                </p>
              </div>
            </div>
            <div className="grid-2">
              {[
                [
                  'business',
                  '经营资质补充核验',
                  '提交营业执照、行业资质或其他可核验的经营证明；个人创作者可按实际情况不申请此项。',
                ],
                [
                  'portfolio',
                  '作品真实性核验',
                  '填写可核验的作品来源、客户授权编号或其他真实性说明。',
                ],
              ].map(([type, title, description]) => {
                const current = verifications.data?.find((v) => v.type === type);
                return (
                  <div className="panel" key={type} style={{ boxShadow: 'none' }}>
                    <div className="row spread">
                      <strong>{title}</strong>
                      <Status
                        value={current?.status || 'pending'}
                        label={current ? undefined : '未申请'}
                      />
                    </div>
                    <p className="small muted" style={{ margin: '12px 0' }}>
                      {description}
                    </p>
                    {current?.review_reason && (
                      <p className="small muted">审核意见：{current.review_reason}</p>
                    )}
                    {profile && current?.status !== 'verified' && (
                      <ActionDialog
                        title={current ? `更新${title}申请` : `申请${title}`}
                        trigger={current ? '重新提交' : '提交核验'}
                        variant="secondary"
                        path="/provider-verifications"
                        fields={[
                          {
                            name: 'reference',
                            label: '核验说明 / 凭证编号',
                            type: 'textarea',
                            min: 2,
                            value: current?.reference,
                          },
                        ]}
                        transform={(v) => ({ type, reference: v.reference })}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </section>
          <section className="panel" style={{ marginTop: 24 }}>
            <div className="panel-title">
              <div>
                <h3>服务产品</h3>
                <p className="small muted" style={{ marginTop: 7 }}>
                  把常见项目整理成半标准化服务，让客户先理解交付范围、周期和价格，再进入定制沟通。
                </p>
              </div>
              {profile?.status === 'verified' && (
                <ActionDialog
                  title="创建服务产品"
                  trigger={
                    <>
                      <Plus size={15} />
                      新服务产品
                    </>
                  }
                  path="/service-packages"
                  fields={packageFields()}
                  transform={servicePackagePayload}
                />
              )}
            </div>
            {profile?.status !== 'verified' && (
              <Notice>服务商资料审核通过后即可创建并公开服务产品。</Notice>
            )}
            <QueryState query={packages}>
              {packages.data?.length ? (
                packages.data.map((entry) => {
                  const pkg = entry.package;
                  return (
                    <div className="list-item" key={pkg.id}>
                      <div className="grow">
                        <div className="row wrap">
                          <strong>{pkg.name}</strong>
                          <Status value={pkg.status} />
                        </div>
                        <p>
                          {pkg.category} ·{' '}
                          {!collaborationOnly &&
                            (pkg.pricing_mode === 'custom'
                              ? '按需求报价'
                              : pkg.pricing_mode === 'fixed'
                                ? money(pkg.price_min_minor)
                                : pkg.pricing_mode === 'starting_from'
                                  ? `${money(pkg.price_min_minor)} 起`
                                  : `${money(pkg.price_min_minor)} – ${money(pkg.price_max_minor)}`)}
                          {' · '}约 {pkg.estimated_days} 天 · {pkg.revision_limit} 轮修改
                        </p>
                        <small className="muted">{pkg.summary}</small>
                      </div>
                      <div className="row wrap">
                        <ActionDialog
                          title="编辑服务产品"
                          trigger="编辑"
                          variant="secondary"
                          method="PUT"
                          path={`/service-packages/${pkg.id}`}
                          fields={packageFields(pkg)}
                          transform={servicePackagePayload}
                          description="编辑会产生新的服务产品版本，并回到草稿状态；重新确认后再发布。"
                        />
                        {pkg.status === 'published' ? (
                          <ActionDialog
                            title="下架服务产品"
                            intent="destructive"
                            submitLabel="确认下架服务产品"
                            trigger="下架"
                            variant="secondary"
                            path={`/service-packages/${pkg.id}/archive`}
                            description="下架后公开主页不再展示，已关联到历史需求的记录不受影响。"
                          />
                        ) : (
                          <ActionDialog
                            title="发布服务产品"
                            trigger="发布"
                            path={`/service-packages/${pkg.id}/publish`}
                            description="发布后客户可以在你的公开服务商主页查看，并以此为基础发布需求。"
                          />
                        )}
                      </div>
                    </div>
                  );
                })
              ) : (
                <Empty
                  title="还没有服务产品"
                  description="把企业宣传片、产品广告或月度短视频等常见业务整理成可比较的标准服务。"
                />
              )}
            </QueryState>
          </section>
          <section className="panel" style={{ marginTop: 24 }}>
            <div className="panel-title">
              <h3>作品案例</h3>
              {profile?.status === 'verified' && canWrite && (
                <PortfolioEditor
                  packages={(packages.data || [])
                    .map((entry) => entry.package)
                    .filter((pkg) => pkg.status === 'published')}
                />
              )}
            </div>
            {profile?.status !== 'verified' && (
              <Notice>服务商资料审核通过后才能提交公开案例，避免案例与未认证主体脱节。</Notice>
            )}
            {cases.data?.items.map((c) => (
              <div
                className="list-item portfolio-work-item"
                key={c.id}
                style={{ alignItems: 'flex-start' }}
              >
                <Image src={portfolioCover(c, 'workspace')} alt={c.title} className="" />
                <div className="grow stack" style={{ gap: 10 }}>
                  <div>
                    <strong>{c.title}</strong>
                    <p>
                      {c.category} · {c.industry} · {portfolioMediaLabel(c)}
                    </p>
                  </div>
                  {!!c.credits?.length && (
                    <div className="row wrap">
                      {c.credits.map((credit) => (
                        <span className="tag" key={credit.id}>
                          {credit.role} · {credit.display_name}
                          {credit.verified ? ' · 已核验' : ''}
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="row wrap">
                    <PortfolioPreview item={c} scope="workspace" />
                    <ActionDialog
                      title="添加案例制作署名"
                      trigger="添加署名"
                      variant="secondary"
                      path={`/cases/${c.id}/credits`}
                      description="补充导演、摄影、后期、场地、器材等制作信息。修改已公开案例后会重新进入审核。"
                      fields={[
                        {
                          name: 'role',
                          label: '角色 / 职务',
                          placeholder: '例如：导演、摄影指导、调色、场地',
                        },
                        {
                          name: 'subject_id',
                          label: '关联自己的影视资源（可选）',
                          required: false,
                          options: [
                            { value: '', label: '不关联平台资源，使用文字署名' },
                            ...(resources.data?.items || [])
                              .filter((resource) => resource.status === 'published')
                              .map((resource) => ({ value: resource.id, label: resource.title })),
                          ],
                        },
                        {
                          name: 'display_name',
                          label: '署名名称',
                          required: false,
                          placeholder: '未关联资源时填写姓名 / 团队 / 场地名称',
                        },
                      ]}
                      transform={(value) => ({
                        role: value.role,
                        subject_type: value.subject_id ? 'resource' : 'external',
                        subject_id: value.subject_id || '',
                        display_name: value.display_name || '',
                      })}
                    />
                    {c.credits
                      ?.filter(
                        (credit) => !(credit.verified && credit.subject_type === 'organization'),
                      )
                      .map((credit) => (
                        <ActionDialog
                          key={credit.id}
                          title={`移除${credit.role}署名`}
                          trigger={`移除 ${credit.display_name}`}
                          variant="secondary"
                          path={`/cases/${c.id}/credits/${credit.id}/remove`}
                          description="移除公开案例中的制作署名后，已公开案例会重新进入审核。"
                          submitLabel="确认移除"
                        />
                      ))}
                  </div>
                  {canWrite && ['draft', 'rejected', 'published'].includes(c.status) && (
                    <PortfolioEditor
                      item={c}
                      packages={(packages.data || [])
                        .map((entry) => entry.package)
                        .filter((pkg) => pkg.status === 'published')}
                    />
                  )}
                </div>
                <Status value={c.status} label={c.status === 'published' ? '已公开' : undefined} />
              </div>
            ))}
            <ErrorNotice error={cases.error} />
            <Pagination
              page={casePage}
              size={cases.data?.page_size || 30}
              total={cases.data?.total || 0}
              onPage={setCasePage}
            />
            {!cases.data?.items.length && (
              <Empty
                title="让作品替你介绍自己"
                description="上传作品视频、照片与创作说明，审核后展示到网站与小程序。"
              />
            )}
          </section>
        </>
      }
    </>
  );
}
